import { createContext, memo, ReactElement, useCallback, useEffect, useRef } from "react";
import { ErrorBoundary } from "src/components/error-handling";
import { FeedCardLayout } from "src/components/feed-card/common/feed-card-layout";
import { useElementFirstSeen } from "src/hooks/use-element-first-seen";
import { FeedCardContent } from "./feed-card-content";
import { FeedCardFooter } from "./feed-card-footer";
import { FeedCardHeader } from "./feed-card-header";
import { FEED_CARD_TEST_ID } from "./feed-card.test-ids";
import { IFeedCard, IFeedCardUserReactionContentReference } from "./types/feed-card";

interface IFeedCardContext {
    contentId: string;
    userReactionContentReferences?: IFeedCardUserReactionContentReference[] | null;
    likeCount?: number | null;
    commentCount?: number | null;
}

export const FeedCardContext = createContext<IFeedCardContext>({ contentId: "" });

export interface IFeedCardProps extends Omit<IFeedCard, "userActivityEntity"> {
    showContextMenu?: boolean;
    contextMenuItems?: ReadonlyArray<ReactElement>;
    onMount?: (element: HTMLElement | null) => void;
    onFirstSeen?: (contentId: string) => void;
}

export const FeedCard = ({
    id,
    userReactionContentReferences,
    actor,
    content,
    articlePreview,
    footer,
    header,
    displayDate,
    onMount,
    onFirstSeen,
    showContextMenu,
    contextMenuItems,
    taggedEntities = null,
}: IFeedCardProps) => {
    const ref = useRef(null);
    const handleOnFirstSeen = useCallback(() => {
        onFirstSeen?.(id);
    }, [id, onFirstSeen]);

    useElementFirstSeen(ref, handleOnFirstSeen);

    useEffect(() => {
        if (onMount) {
            onMount(ref.current);
        }
    }, [onMount]);

    return (
        <ErrorBoundary FallbackComponent={() => <div ref={ref} />}>
            <FeedCardContext.Provider
                value={{ contentId: id, userReactionContentReferences, likeCount: footer?.likeInfo?.count, commentCount: footer?.commentInfo?.count }}
            >
                <FeedCardLayout.Base ref={ref} data-test-id={FEED_CARD_TEST_ID}>
                    {header && (
                        <FeedCardHeader
                            text={header.text}
                            args={header.args}
                            showContextMenu={showContextMenu}
                            contextMenuItems={contextMenuItems}
                            taggedEntities={taggedEntities}
                        />
                    )}
                    <FeedCardContent displayDate={displayDate} actor={actor} content={content} articlePreview={articlePreview} />
                    {footer && (
                        <FeedCardFooter
                            userReactionContentReferences={userReactionContentReferences}
                            likeInfo={footer.likeInfo}
                            link={footer.link}
                            shareInfo={footer.shareInfo}
                            commentInfo={footer.commentInfo}
                        />
                    )}
                </FeedCardLayout.Base>
            </FeedCardContext.Provider>
        </ErrorBoundary>
    );
};

export const MemoizedFeedCard = memo(FeedCard);
